// ============================================================
// KOSHER MODE — filtro + transformación de recetas
// ============================================================
// Diseñado como funciones puras (sin side-effects) para poder
// aplicarse a todas las lecturas de recipes.json de forma
// transparente. NO modifica el JSON original.
//
// Reglas de negocio (definidas por Alan "el Pollo"):
//
//  A) OCULTAR la receta si contiene ingredientes claramente
//     no kosher SIN sustituto viable: cerdo, jamón, panceta,
//     conejo, jabalí, mariscos/moluscos/crustáceos, avestruz,
//     caballo.
//
//  B) SUSTITUIR ingredientes que sí tienen versión kosher:
//     - Cangrejo → kanikama / surimi kosher
//     - Tocino → tocino de ave, vacuno o cordero
//     - Lácteos (leche, crema, yogurt, queso, mantequilla,
//       ricotta, mascarpone, leche condensada) → versión parve.
//     - Carne molida (solo si la receta también tiene QUESO):
//       → carne molida vegetal (base plantas).
//
//  C) LÓGICA CARNE + LÁCTEO:
//     - Si la receta mezcla CARNE + QUESO específicamente y usa
//       carne MOLIDA/picada/hamburguesa/albóndiga/kibbeh/kefta:
//       cambiamos la carne por vegetal → receta queda LECHE.
//     - En cualquier otro caso de carne + lácteo:
//       cambiamos los lácteos por parve → receta queda CARNE.
//
//  D) ETIQUETA final (siempre visible en modo kosher):
//     - CARNE  → tiene carne, no tiene lácteo
//     - LECHE  → tiene lácteo, no tiene carne (o tuvo carne
//                que se cambió a vegetal por queso)
//     - PARVE  → no tiene carne ni lácteo
// ============================================================

// -- INGREDIENTES QUE OCULTAN LA RECETA (sin sustituto) -----
// Se busca la palabra como token entero (no substring) usando
// una regex \b. Todo se compara sin tildes y en minúsculas.
const HIDDEN_KEYWORDS = [
  // Cerdo y derivados
  "cerdo", "chancho", "chorizo", "jamon serrano", "jamon iberico",
  "prosciutto", "panceta", "pancetta", "guanciale", "pepperoni",
  "salchicha alemana", "salame", "salami", "mortadela",
  // Otras carnes no kosher
  "conejo", "jabali", "caballo", "avestruz",
  // Mariscos / moluscos / crustáceos
  "camaron", "camarones", "langosta", "langostino", "langostinos",
  "ostra", "ostras", "ostion", "ostiones", "vieira", "vieiras",
  "mejillon", "mejillones", "choro", "choros", "chorito", "choritos",
  "almeja", "almejas", "macha", "machas",
  "erizo", "erizos", "pulpo", "calamar", "calamares", "jibia",
  "sepia", "gamba", "gambas",
  // Preparaciones que implican productos prohibidos
  "carbonara", // usa panceta/guanciale por definición
];

// -- SUSTITUCIONES DE INGREDIENTES ---------------------------
// Cada entrada: { match: RegExp, replace: (originalText) => string }
// La regex se aplica sobre el ingrediente NORMALIZADO
// (minúsculas, sin tildes). Para el reemplazo trabajamos sobre
// el texto original preservando el contexto original.
const INGREDIENT_SUBSTITUTIONS = [
  // Cangrejo → kanikama / surimi kosher
  {
    match: /\bcangrejo(s)?\b/,
    replace: (t) => t.replace(/\bcangrejo(s)?\b/gi, "kanikama o surimi kosher"),
    tag: "cangrejo→kanikama",
  },
  { match: /\bjaiba(s)?\b/, replace: (t) => t.replace(/\bjaiba(s)?\b/gi, "kanikama o surimi kosher"), tag: "jaiba→kanikama" },
  { match: /\bcentolla(s)?\b/, replace: (t) => t.replace(/\bcentolla(s)?\b/gi, "kanikama o surimi kosher"), tag: "centolla→kanikama" },
  // Tocino → tocino de ave o vacuno
  { match: /\btocino\b/, replace: (t) => t.replace(/\btocino\b/gi, "tocino de ave o vacuno (kosher)"), tag: "tocino→ave" },
  { match: /\bbacon\b/, replace: (t) => t.replace(/\bbacon\b/gi, "tocino de ave o vacuno (kosher)"), tag: "bacon→ave" },
];

// -- DETECCIÓN DE INGREDIENTES LÁCTEOS ------------------------
// Cada entrada tiene su versión parve como reemplazo.
const DAIRY_MAP = [
  // Queso (importante detectar por separado — activa la regla de "molida + queso → cambio carne")
  { match: /\bqueso\b/,          replace: "queso vegano",                     isQueso: true },
  { match: /\bricotta\b/,        replace: "tofu blando batido (sustituto parve)", isQueso: true },
  { match: /\bmascarpone\b/,     replace: "queso crema parve",                isQueso: true },
  { match: /\brequeson\b/,       replace: "requesón parve (tofu batido)",     isQueso: true },
  { match: /\bparmesano\b/,      replace: "parmesano vegano",                 isQueso: true },
  { match: /\bmozzarella\b/,     replace: "mozzarella vegana",                isQueso: true },
  { match: /\bfeta\b/,           replace: "queso feta vegano",                isQueso: true },
  { match: /\bcheddar\b/,        replace: "cheddar vegano",                   isQueso: true },
  { match: /\bemmental\b/,       replace: "emmental vegano",                  isQueso: true },
  { match: /\bgruy(e|é)re?\b/,   replace: "gruyère vegano",                   isQueso: true },
  // Crema
  { match: /\bcrema espesa\b/,   replace: "crema vegetal parve (tipo Rich's)" },
  { match: /\bcrema doble\b/,    replace: "crema vegetal parve (tipo Rich's)" },
  { match: /\bcrema (fresca|liquida|batida|de leche|para batir|de cocina)\b/, replace: "crema vegetal parve (tipo Rich's)" },
  { match: /\bnata\b/,           replace: "crema vegetal parve (tipo Rich's)" },
  { match: /\bcrema\b/,          replace: "crema vegetal parve (tipo Rich's)" },
  // Yogurt
  { match: /\byogur(t)?\b/,      replace: "yogurt vegetal (soja o coco)" },
  // Leche condensada
  { match: /\bleche condensada\b/, replace: "leche condensada de coco" },
  { match: /\bleche evaporada\b/,  replace: "leche evaporada de coco" },
  // Mantequilla
  { match: /\bmantequilla\b/,    replace: "margarina parve" },
  { match: /\bmanteca\b/,        replace: "margarina parve" },
  // Leche (última pa' no chocar con "leche condensada/evaporada")
  { match: /\bleche\b/,          replace: "leche vegetal (soja o avena)" },
];

// -- DETECCIÓN DE CARNES -------------------------------------
const MEAT_KEYWORDS = /\b(vacuno|res\b|buey|ternera|carne(?!\s+vegetal)|cordero|pollo|pavo|pato|codorniz|higado|molleja|osobuco|lomo\s+vetado|entrana|asado|churrasco|filete\s+de\s+(vacuno|res|carne|ternera)|filete\s+mignon|solomillo)\b/;

// Palabras que indican carne MOLIDA (activa regla de "cambio a vegetal solo si hay queso").
const GROUND_MEAT_KEYWORDS = /\b(molida|picada|hamburguesa|albondiga(s)?|kibbeh?|kefta|kafta|kibe|pastel de carne|bolognesa|bolognese|ragu|ragout|pino)\b/;

// -- SUSTITUCIÓN DE CARNE MOLIDA POR VEGETAL -----------------
// Se aplica solo cuando la receta también tiene queso.
function substituteGroundMeat(ingText) {
  // Detecta cualquier mención a carne molida / picada / etc y la reemplaza por vegetal
  let out = ingText;
  out = out.replace(/\bcarne (de )?(vacuno|res|buey|ternera) molida\b/gi, "carne vegetal molida (base plantas, tipo NotBeef)");
  out = out.replace(/\bcarne molida\b/gi, "carne vegetal molida (base plantas, tipo NotBeef)");
  out = out.replace(/\bpollo molido\b/gi, "pollo vegetal molido (tipo NotChicken)");
  out = out.replace(/\bpavo molido\b/gi, "pavo vegetal molido (tipo NotChicken)");
  out = out.replace(/\bcarne picada\b/gi, "carne vegetal molida (base plantas, tipo NotBeef)");
  return out;
}

// ============================================================
// HELPERS internos
// ============================================================
function normalize(s) {
  return String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function ingredientList(recipe) {
  return Array.isArray(recipe.ingredientes) ? recipe.ingredientes : [];
}

function joinedNormalized(recipe) {
  // Concatenamos nombre + ingredientes + descripción pa' detección global
  return normalize([
    recipe.nombre || "",
    recipe.descripcion_corta || "",
    ...ingredientList(recipe),
  ].join(" \n "));
}

function hasHiddenIngredient(recipe) {
  const text = joinedNormalized(recipe);
  return HIDDEN_KEYWORDS.some((kw) => new RegExp(`\\b${kw}\\b`).test(text));
}

function hasDairy(ingText) {
  const n = normalize(ingText);
  return DAIRY_MAP.some((d) => d.match.test(n));
}

function hasCheese(ingText) {
  const n = normalize(ingText);
  return DAIRY_MAP.some((d) => d.isQueso && d.match.test(n));
}

function hasMeat(ingText) {
  return MEAT_KEYWORDS.test(normalize(ingText));
}

function hasGroundMeat(ingText) {
  const n = normalize(ingText);
  return GROUND_MEAT_KEYWORDS.test(n) && hasMeat(n);
}

// ============================================================
// API PÚBLICA
// ============================================================

// Devuelve la etiqueta {label, tone} de una receta transformada.
// IMPORTANTE: nunca devuelve MIXTA. Si detecta carne + lácteo (caso que
// no debería ocurrir después de las transformaciones), decide por defecto:
// prefiere CARNE (típicamente la carne es el plato principal).
export function computeKosherLabel(recipe) {
  const joined = ingredientList(recipe).join(" \n ");
  const meat = hasMeat(joined);
  const dairy = hasDairy(joined);
  if (meat && !dairy) return { label: "CARNE", tone: "carne" };
  if (dairy && !meat) return { label: "LECHE", tone: "leche" };
  if (meat && dairy)  return { label: "CARNE", tone: "carne" }; // fallback — carne manda
  return { label: "PARVE", tone: "parve" };
}

// Aplica todas las transformaciones a UNA receta.
// Devuelve { recipe: nuevaReceta, label, transformed:boolean }.
// Si la receta debe ocultarse en modo kosher, devuelve null.
export function transformRecipeKosher(recipe) {
  // 1) Filtro de ingredientes prohibidos → ocultar completamente
  if (hasHiddenIngredient(recipe)) return null;

  const originalIngredients = ingredientList(recipe);
  // 2) Sustituciones base (cangrejo, tocino) — se aplican siempre
  let ingredients = originalIngredients.map((ing) => {
    let out = ing;
    for (const sub of INGREDIENT_SUBSTITUTIONS) {
      if (sub.match.test(normalize(out))) {
        out = sub.replace(out);
      }
    }
    return out;
  });

  // 3) Lógica carne + lácteo — se elige un lado y se fuerza el label final,
  //    para GARANTIZAR que nunca queda MIXTA.
  const joinedText = ingredients.join(" \n ");
  const meat = hasMeat(joinedText);
  const dairy = hasDairy(joinedText);
  const cheese = hasCheese(joinedText);
  const groundMeat = hasGroundMeat(joinedText);

  let forcedLabel = null;
  if (meat && dairy) {
    if (groundMeat && cheese) {
      // Regla: molida + queso → cambiar CARNE por vegetal → receta queda LECHE
      ingredients = ingredients.map((ing) => substituteGroundMeat(ing));
      forcedLabel = { label: "LECHE", tone: "leche" };
    } else {
      // Cualquier otro caso: cambiar LÁCTEOS por parve → receta queda CARNE
      ingredients = ingredients.map((ing) => {
        let out = ing;
        for (const d of DAIRY_MAP) {
          if (d.match.test(normalize(out))) {
            out = out.replace(new RegExp(d.match.source, "gi"), d.replace);
          }
        }
        return out;
      });
      forcedLabel = { label: "CARNE", tone: "carne" };
    }
  }

  // 4) Detectar si algo cambió
  const transformed = ingredients.some((v, i) => v !== originalIngredients[i]);

  // 5) Etiqueta final: si hicimos una decisión en el paso 3, se respeta.
  //    Si no, computamos normal (garantizamos que no salga MIXTA).
  const label = forcedLabel || computeKosherLabel({ ...recipe, ingredientes: ingredients });

  return {
    recipe: { ...recipe, ingredientes: ingredients },
    label,
    transformed,
  };
}

// Aplica el filtro + transformación a un array de recetas.
// Devuelve { visible: [recetas transformadas con .kosher_label], hiddenCount, transformedCount }.
export function applyKosherMode(recipes, isKosherActive) {
  if (!isKosherActive) {
    // No aplicar nada. Pero igual computamos label para poder mostrarlo si el usuario quiere (opcional).
    return {
      visible: recipes,
      hiddenCount: 0,
      transformedCount: 0,
    };
  }
  const visible = [];
  let hiddenCount = 0;
  let transformedCount = 0;
  for (const r of recipes) {
    const res = transformRecipeKosher(r);
    if (!res) { hiddenCount++; continue; }
    visible.push({
      ...res.recipe,
      kosher_label: res.label,
      kosher_transformed: res.transformed,
    });
    if (res.transformed) transformedCount++;
  }
  return { visible, hiddenCount, transformedCount };
}

// Transforma UNA receta (para uso en RecipeDetail / Cook).
// Retorna la receta con .kosher_label agregado, o null si debe ocultarse.
export function transformOne(recipe, isKosherActive) {
  if (!isKosherActive) return recipe;
  const res = transformRecipeKosher(recipe);
  if (!res) return null;
  return { ...res.recipe, kosher_label: res.label, kosher_transformed: res.transformed };
}

// Helper para leer el estado kosher desde localStorage.
// Key: recetario_kosher_{member} para papa/mama/tati/lucho/tairuchi/liuchi
//      recetario_kosher_invitado_{guestName} para invitados
export function readKosherMode(member, guestName) {
  if (typeof localStorage === "undefined") return false;
  if (member === "invitado") {
    if (!guestName) return false;
    const key = `recetario_kosher_invitado_${guestName.trim().toLowerCase()}`;
    return localStorage.getItem(key) === "true";
  }
  return localStorage.getItem(`recetario_kosher_${member}`) === "true";
}

export function writeKosherMode(member, guestName, value) {
  if (typeof localStorage === "undefined") return;
  const key = member === "invitado"
    ? `recetario_kosher_invitado_${(guestName || "").trim().toLowerCase()}`
    : `recetario_kosher_${member}`;
  if (value) localStorage.setItem(key, "true");
  else localStorage.removeItem(key);
}
